import { useLocation } from 'react-router-dom';
import { useDeliveryLocation } from '@/contexts/DeliveryLocationContext';
import { egyptianLocations } from '@/data/locations';

interface CityIntroParagraphProps {
  language: string;
}

/**
 * ~150-word city-specific paragraph that names 5–6 real neighborhood
 * names from locations.ts — boosts body word count, internal-link
 * discovery, and entity signal vs Floward's Alexandria page.
 *
 * Originally lived under the H1 in FlowerAuraCategories.tsx. Moved to
 * sit under the "Flower Guides for {city}" section so the top of the
 * page can stay visual / above-the-fold and the long-form copy reads
 * as a closing summary (which is how Floward, Floom, and other
 * competitors typically structure it).
 *
 * Resolves the active city in the same way FlowerAuraCategories does:
 * 1) /:lang/:citySlug in the URL → that city
 * 2) Otherwise → DeliveryLocationContext.selectedCity
 * 3) Otherwise → fall back to a generic "across Egypt" paragraph
 */
const CityIntroParagraph = ({ language }: CityIntroParagraphProps) => {
  const location = useLocation();
  const { selectedCity } = useDeliveryLocation();

  // Match /en-eg/:citySlug or /ar-eg/:citySlug. Trailing path segments
  // (like /products) are fine to ignore — the city is the first segment
  // after the locale.
  const homeCityMatch = location.pathname.match(/^\/(?:en-eg|ar-eg)\/([^/]+)/);
  const segment = homeCityMatch?.[1];
  const cityFromPath = segment
    ? egyptianLocations.find((c) => c.slug === segment)
    : undefined;
  const city: any = cityFromPath ?? (!segment ? selectedCity : null);

  const cityNameEn = city?.name_en || city?.name;
  const cityNameAr = city?.name_ar;
  const areaSamples = (city?.areas || []).slice(0, 6);

  const areaListEn = areaSamples
    .map((a: any) => a.name_en || a.name)
    .filter(Boolean)
    .join(', ');
  const areaListAr = areaSamples
    .map((a: any) => a.name_ar || a.name_en || a.name)
    .filter(Boolean)
    .join('، ');

  const introEn = cityNameEn
    ? `Bouqueh delivers fresh, hand-arranged flower bouquets across every ${cityNameEn} neighborhood${
        areaListEn ? ` — from ${areaListEn} and more` : ''
      }. Order before 5 PM for same-day delivery to apartments, offices and hotels. Every arrangement is built by our local florists the same morning, wrapped, and brought to your door by our ${cityNameEn}-based delivery team. Birthday, anniversary, sympathy or just-because — we cover the whole city with reliable cash-on-delivery and secure card payment through Paymob, plus a fresh-flowers guarantee on every order.`
    : 'Bouqueh delivers fresh, hand-arranged flower bouquets across 28 Egyptian cities and 200+ named neighborhoods. Same-day delivery before 5 PM, secure card or cash-on-delivery, and a fresh-flowers guarantee on every order.';

  const introAr = cityNameAr
    ? `بوكيه بيوصّل ورد فريش متنسق باليد لكل منطقة في ${cityNameAr}${
        areaListAr ? ` — من ${areaListAr} وكمان مناطق تانية` : ''
      }. اطلب قبل 5 العصر وهنوصّلك في نفس اليوم للشقق والمكاتب والفنادق. كل تنسيق بنرتبه صبح يومه على إيد بائعينا في ${cityNameAr}، بنلفه ونوصله لباب البيت. عيد ميلاد، ذكرى سنوية، عزاء، أو من غير مناسبة — بنغطي المدينة كلها بالدفع كاش عند التوصيل أو بالكارت أونلاين عن طريق Paymob، وضمان ورد فريش في كل طلب.`
    : 'بوكيه بيوصّل ورد فريش متنسق باليد في 28 مدينة مصرية وأكتر من 200 منطقة. توصيل في نفس اليوم لحد 5 العصر، دفع كاش أو بالكارت، وضمان ورد فريش في كل طلب.';

  const intro = language === 'ar' ? introAr : introEn;

  return (
    <section className="py-10 bg-white">
      <div className="container mx-auto px-4">
        <p
          className="text-base text-muted-foreground max-w-3xl mx-auto leading-relaxed text-left sm:text-center"
          dir={language === 'ar' ? 'rtl' : 'ltr'}
        >
          {intro}
        </p>
      </div>
    </section>
  );
};

export default CityIntroParagraph;
